/* Wicked Clothes — Scroll Restore
 * ─────────────────────────────────────────────────────────────────────
 * Saves scrollY on the way out (pagehide / visibilitychange),
 * restores it on the way back in. Targeted at the swipe-back flow
 * and the standard browser back button.
 *
 * Loaded site-wide via theme.liquid so every page captures its scroll
 * position on exit, and every page's restoration runs on entry.
 *
 * Key observations:
 *   1. Restoring scroll requires `history.scrollRestoration = 'manual'`
 *      ONLY for fresh loads (so Shopify's default browser behavior
 *      doesn't fight us). Reset to 'auto' between pages so bfcache can
 *      restore scroll natively where supported.
 *   2. bfcache pageshow doesn't run any module-level code — only
 *      registered event listeners fire. Re-restore inside the pageshow
 *      handler when persisted=true.
 *   3. Multiple retries cover late-loading content (lazy images,
 *      collection grid expansion, etc.) that change document height
 *      after the initial scroll attempt.
 *   4. (cycle 13 tweak) The key is computed when it is used, so a URL
 *      changed by pushState (Dawn's AJAX sort) saves under the URL the
 *      shopper will come back to. A cold load restores only on a Back /
 *      Forward or a reload; a plain link to the same URL starts at the
 *      top and drops the stale position.
 */
(function () {
  'use strict';

  if (window.__wcScrollRestore) return;
  window.__wcScrollRestore = true;

  function key() { return 'wc-scroll:' + location.pathname + location.search; }
  var RETRY_DELAYS = [0, 80, 180, 320, 600, 1000, 1800, 2800];

  function readSaved() {
    try {
      var raw = sessionStorage.getItem(key());
      if (!raw) return 0;
      var y = parseInt(raw, 10);
      return (!isNaN(y) && y > 0) ? y : 0;
    } catch (e) { return 0; }
  }

  function restoreTo(y) {
    if (!y) return;
    var KEY = key();
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

    // Abortable retry chain. The first user interaction (touch, wheel,
    // scroll-key) cancels all pending retries — otherwise late retries
    // ride over the user's scroll attempts and look like the page is
    // "fighting" them / flashing back. Without this, fixing the late-
    // load layout-shift case hostage-takes the user's first scroll.
    var aborted = false;
    var timers = [];
    var fire = function () { if (!aborted) window.scrollTo(0, y); };

    function cleanup() {
      window.removeEventListener('wheel', abort);
      window.removeEventListener('touchmove', abort);
      window.removeEventListener('keydown', onKey);
    }
    function abort() {
      if (aborted) return;
      aborted = true;
      timers.forEach(clearTimeout);
      cleanup();
    }
    function onKey(e) {
      var k = e.key;
      if (k === 'ArrowUp' || k === 'ArrowDown' || k === 'PageUp' ||
          k === 'PageDown' || k === ' ' || k === 'End' || k === 'Home') {
        abort();
      }
    }
    window.addEventListener('wheel',     abort, { passive: true });
    window.addEventListener('touchmove', abort, { passive: true });
    window.addEventListener('keydown',   onKey);

    // Immediate fire so the first paint lands at the right position.
    fire();
    RETRY_DELAYS.forEach(function (d) {
      if (d === 0) {
        requestAnimationFrame(function () {
          requestAnimationFrame(function () { fire(); });
        });
      } else {
        timers.push(setTimeout(fire, d));
      }
    });
    // Final cleanup after the last retry: clear sessionStorage so a
    // future same-tab navigation doesn't restore stale scroll, reset
    // scrollRestoration to auto, and tear down listeners.
    setTimeout(function () {
      if (!aborted) { aborted = true; cleanup(); }
      try { sessionStorage.removeItem(KEY); } catch (e) {}
      if ('scrollRestoration' in history) history.scrollRestoration = 'auto';
    }, RETRY_DELAYS[RETRY_DELAYS.length - 1] + 200);
  }

  function save() {
    try {
      var sy = window.scrollY || window.pageYOffset || 0;
      if (sy > 0) sessionStorage.setItem(key(), String(sy));
    } catch (e) {}
  }

  // Initial load: restore only when the shopper is coming BACK (or reloading).
  // Unknown navigation type (very old browsers) keeps the old behaviour.
  function navType() {
    try {
      var nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
      if (nav && nav.type) return nav.type;
      if (performance.navigation) return ['navigate', 'reload', 'back_forward'][performance.navigation.type] || '';
    } catch (e) {}
    return '';
  }
  var nt = navType();
  if (!nt || nt === 'back_forward' || nt === 'reload') restoreTo(readSaved());
  else { try { sessionStorage.removeItem(key()); } catch (e) {} }

  // bfcache restore: pageshow with persisted=true. The browser may
  // have restored scroll natively, but if we'd previously set
  // scrollRestoration=manual it might have skipped restoration. Re-
  // read and restore to be safe.
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) {
      var y = readSaved();
      if (y) restoreTo(y);
    }
  });

  // Save on navigate-away — pagehide is the most reliable.
  // visibilitychange covers iOS background / Safari tab-switch cases
  // where pagehide fires inconsistently.
  window.addEventListener('pagehide', save);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') save();
  });
})();
